package com.schoolconnect.app.feature.exams

import com.schoolconnect.app.feature.common.PaginationPolicy

data class ExamsFilters(
    val status: String = "",
    val classSectionId: String = ""
)

data class ExamRowUi(
    val id: String,
    val name: String,
    val type: String,
    val classes: String,
    val startDate: String,
    val status: String,
    val endDate: String = "",
    val resultStatus: String = "Not Started",
    val hasOverallGrade: Boolean = true,
    val hasOverallRank: Boolean = true,
    val hasOverallMarks: Boolean = true,
    val subjectConfigs: Map<String, ExamSubjectSelectionUi> = emptyMap(),
    val maxMarks: Double? = null,
    val classSectionId: String = ""
    ,val backendId: String = id
)

data class ExamSubjectSelectionUi(
    val hasGrade: Boolean = false,
    val hasRank: Boolean = false,
    val hasMarks: Boolean = false,
    val internalMark: String = "",
    val mainMark: String = "",
    val totalMark: String = ""
)

data class ExamResultStudentUi(
    val key: String,
    val studentId: String = key,
    val roll: String,
    val name: String,
    val grade: String = "",
    val percent: String = "",
    val total: String = "",
    val rank: String = "",
    val notes: String = "",
    val subjectValues: Map<String, String> = emptyMap()
)

data class ExamSubjectMarkDraft(
    val subjectId: String,
    val internal: Double,
    val main: Double,
    val total: Double,
    val grade: String? = null,
    val rank: Int? = null
)

data class ExamResultEntryDraft(
    val studentId: String,
    val rollNumber: String?,
    val name: String?,
    val total: Double,
    val totalPercentage: Double,
    val grade: String?,
    val rank: Int?,
    val notes: String?,
    val subjectMarks: List<ExamSubjectMarkDraft>
)

data class ExamResultPreviewUi(
    val rollNumber: String,
    val studentName: String,
    val subject: String,
    val marks: String,
    val totalMarks: String,
    val percentage: String,
    val grade: String,
    val rank: String,
    val notes: String
    ,val subjects: List<ExamSubjectPreviewUi>
)

data class ExamSubjectPreviewUi(
    val name: String,
    val marks: String,
    val totalMarks: String,
    val percentage: String
)

data class ExamInsightsUi(
    val totalStudents: Int,
    val averagePercentage: String,
    val status: String,
    val classLabel: String,
    val buckets: List<ExamInsightBucketUi>,
    val topStudents: List<ExamInsightStudentUi>,
    val bottomStudents: List<ExamInsightStudentUi>
)

data class ExamInsightBucketUi(
    val label: String,
    val count: Int,
    val percentage: String
)

data class ExamInsightStudentUi(
    val rank: String,
    val rollNumber: String,
    val name: String,
    val totalMarks: String,
    val percentage: String
)

data class ExamSubjectDraft(
    val subjectId: String,
    val internalMark: String,
    val mainMark: String,
    val totalMark: String,
    val hasGrade: Boolean,
    val hasRank: Boolean,
    val hasMarks: Boolean
)

data class ExamDraft(
    val title: String,
    val examType: String,
    val classSectionIds: List<String>,
    val startDate: String,
    val endDate: String,
    val status: String,
    val examId: String,
    val subjectMarkConfigs: List<ExamSubjectDraft>,
    val hasOverallGrade: Boolean,
    val hasOverallRank: Boolean,
    val hasOverallMarks: Boolean
)

data class ExamsUiState(
    val isLoading: Boolean = false,
    val isStatisticsLoading: Boolean = true,
    val statisticsErrorMessage: String = "",
    val errorMessage: String = "",
    val saveErrorMessage: String = "",
    val searchQuery: String = "",
    val filters: ExamsFilters = ExamsFilters(),
    val rows: List<ExamRowUi> = emptyList(),
    val totalCount: Int = 0,
    val totalExams: Int = 0,
    val upcomingCount: Int = 0,
    val ongoingCount: Int = 0,
    val completedCount: Int = 0,
    val classOptionIds: Map<String, String> = emptyMap(),
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val sortField: ExamsSortField = ExamsSortField.ID,
    val sortAscending: Boolean = true,
    val isSaving: Boolean = false,
    val saveGeneration: Int = 0,
    val subjectOptionIds: Map<String, String> = emptyMap(),
    val subjectNamesById: Map<String, String> = emptyMap(),
    val subjectCodesById: Map<String, String> = emptyMap(),
    val examTypeOptions: List<String> = emptyList(),
    val examStatusOptions: List<String> = emptyList(),
    val isLoadingSubjects: Boolean = false,
    val selectedResultsExam: ExamRowUi? = null,
    val results: List<ExamResultPreviewUi> = emptyList(),
    val isLoadingResults: Boolean = false,
    val resultsError: String = "",
    val selectedInsightsExam: ExamRowUi? = null
    ,val insights: ExamInsightsUi? = null
    ,val isLoadingInsights: Boolean = false
    ,val insightsError: String = ""
    ,val selectedExamDetails: ExamRowUi? = null
    ,val isLoadingExamDetails: Boolean = false
    ,val examDetailsError: String = ""
    ,val selectedEditExam: ExamRowUi? = null
    ,val isLoadingEditExam: Boolean = false
    ,val editExamError: String = ""
    ,val selectedEditResultsExam: ExamRowUi? = null
    ,val isLoadingEditResults: Boolean = false
    ,val editResultsError: String = ""
    ,val editResultsSubjectNameError: String = ""
    ,val isLoadingImportExport: Boolean = false
    ,val importExportError: String = ""
    ,val editResultsStudentCount: Int = 0
    ,val editResultsStudents: List<ExamResultStudentUi> = emptyList()
)

sealed interface ExamsUiEvent {
    data object AddRequested : ExamsUiEvent
    data class ClassSelected(val classLabel: String) : ExamsUiEvent
    data class SearchChanged(val query: String) : ExamsUiEvent
    data class FiltersChanged(val filters: ExamsFilters) : ExamsUiEvent
    data object FiltersApplied : ExamsUiEvent
    data object FiltersReset : ExamsUiEvent
    data object RefreshClicked : ExamsUiEvent
    data class PageChanged(val pageIndex: Int) : ExamsUiEvent
    data class PageSizeChanged(val pageSize: Int) : ExamsUiEvent
    data class DeleteClicked(val id: String) : ExamsUiEvent
    data class AddExam(val name: String, val type: String, val classes: String, val date: String) : ExamsUiEvent
    data class CreateExam(val draft: ExamDraft) : ExamsUiEvent
    data class UpdateExam(
        val id: String,
        val name: String,
        val type: String,
        val classes: String,
        val startDate: String,
        val endDate: String,
        val status: String,
        val maxMarks: Double? = null,
        val hasOverallGrade: Boolean = false,
        val hasOverallRank: Boolean = false,
        val hasOverallMarks: Boolean = false,
        val subjectMarkConfigs: List<ExamSubjectDraft> = emptyList()
    ) : ExamsUiEvent
    data class SortChanged(val field: ExamsSortField) : ExamsUiEvent
    data class ViewResultsClicked(val exam: ExamRowUi) : ExamsUiEvent
    data class ViewExamDetailsClicked(val exam: ExamRowUi) : ExamsUiEvent
    data class EditExamClicked(val exam: ExamRowUi) : ExamsUiEvent
    data object CloseExamDetails : ExamsUiEvent
    data object CloseEditExam : ExamsUiEvent
    data class EditResultsClicked(val exam: ExamRowUi) : ExamsUiEvent
    data class DownloadImportTemplateClicked(val examId: String) : ExamsUiEvent
    data class DownloadResultsExcelClicked(val examId: String) : ExamsUiEvent
    data class ImportExamResultsClicked(val examId: String, val fileName: String, val bytes: ByteArray) : ExamsUiEvent
    data class SaveExamResultsClicked(
        val examId: String,
        val classSectionId: String,
        val status: String,
        val results: List<ExamResultEntryDraft>
    ) : ExamsUiEvent
    data object CloseEditResults : ExamsUiEvent
    data object CloseResults : ExamsUiEvent
    data class ViewInsightsClicked(val exam: ExamRowUi) : ExamsUiEvent
    data object CloseInsights : ExamsUiEvent
}

internal fun dummyExamResults(): List<ExamResultPreviewUi> = listOf(
    preview("01", "Aarav Sharma", "91", "91%", "A1", "1", "Excellent performance.", listOf("18" to "90%", "19" to "95%", "A1" to "2", "A2" to "3")),
    preview("02", "Diya Patel", "86", "86%", "A2", "3", "Good overall performance.", listOf("17" to "85%", "18" to "90%", "A2" to "4", "A2" to "5")),
    preview("03", "Rahul Mehta", "78", "78%", "B1", "8", "Absent for Science.", listOf("16" to "80%", "15" to "75%", "AB" to "AB", "B1" to "8")),
    preview("04", "Ananya Singh", "89", "89%", "A1", "2", "Very consistent.", listOf("18" to "90%", "18" to "90%", "A1" to "3", "A1" to "2")),
    preview("05", "Kabir Verma", "71", "71%", "B2", "12", "Needs improvement in English.", listOf("14" to "70%", "15" to "75%", "B2" to "10", "B1" to "9")),
    preview("06", "Meera Iyer", "84", "84%", "A2", "5", "Good work.", listOf("17" to "85%", "17" to "85%", "A2" to "5", "A2" to "4"))
)

private fun preview(roll: String, name: String, total: String, percentage: String, grade: String, rank: String, notes: String, values: List<Pair<String, String>>) =
    ExamResultPreviewUi(roll, name, "English", values.first().first, "20", percentage, grade, rank, notes, values.mapIndexed { index, value -> ExamSubjectPreviewUi(listOf("English", "Mathematics", "Science", "Social Science")[index], value.first, "20", value.second) })

enum class ExamsSortField { ID, NAME, TYPE, CLASSES, START_DATE, RESULTS_ENTERED, STATUS }

/** Query keys for the Exams columns; unsupported keys are tracked in the backend requirements draft. */
internal fun ExamsSortField.toApiSortField(): String = when (this) {
    ExamsSortField.ID -> "exam_id"
    ExamsSortField.NAME -> "title"
    ExamsSortField.TYPE -> "exam_type"
    ExamsSortField.CLASSES -> "class"
    ExamsSortField.START_DATE -> "start_date"
    ExamsSortField.RESULTS_ENTERED -> "results_entered"
    ExamsSortField.STATUS -> "status"
}

// Kept for the Exam Results scaffold until that screen receives its own REST
// integration; the Exams Manage page no longer uses this data.
internal fun dummyExamRows(): List<ExamRowUi> = listOf(
    ExamRowUi("EX-0001", "Unit Test - 1", "Unit Test", "8-A", "14 SEP 2026", "Upcoming", "15 SEP 2026"),
    ExamRowUi("EX-0002", "Mid-Term Examination", "Mid-Term", "9-A", "20 SEP 2026", "Upcoming", "25 SEP 2026"),
    ExamRowUi("EX-0003", "Final Examination", "Final Exam", "10-A", "10 MAR 2027", "Completed", "20 MAR 2027")
)
